/* Copyright 2023 (Unpublished) Verto Inc. */

import { HttpClient } from '@angular/common/http';
import { Component, Injectable, OnInit } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { WorkflowService } from '@root/services/workflow.service';
import { NgxObjectDiffService } from '@root/vendor/ngx-object-diff-master/projects/ngx-object-diff/src/lib/ngx-object-diff.service';
import { Observable, of } from 'rxjs';
import { map, switchMap, finalize } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { ActivatedRoute, Router } from '@angular/router';

class WorkflowFile {
  constructor(
    public filename: string,
    public path: string,
    public config: any
  ) {}

  get id(): string {
    return this.config.id;
  }

  get name(): string {
    return this.config.name;
  }
}

@Injectable({
  providedIn: 'root',
})
export class PacksService {
  constructor(private _httpClient: HttpClient) {}

  getLastLoadedPack(): Observable<any> {
    return this._httpClient.get<any>(
      environment.settings.backendUrl + '/api/admin/v2/last_loaded_pack'
    );
  }

  getClients(branch: string, pack: string): Observable<any> {
    return this._httpClient.get<any>(
      environment.settings.backendUrl + '/api/admin/v2/pack_client',
      { params: { branch, pack } }
    );
  }

  getPackNames(branch: string): Observable<any> {
    return this._httpClient.get<any>(
      environment.settings.backendUrl + '/api/admin/v2/pack_names',
      { params: { branch } }
    );
  }

  getWorkflows(branch: string, packName: string, clientName?): Observable<any> {
    const params = {
      name: packName,
      branch,
    };
    if (clientName) {
      params['client'] = clientName;
    }

    return this._httpClient.get<any>(
      environment.settings.backendUrl + '/api/admin/v2/packs',
      { params }
    );
  }
}

@Component({
  selector: 'app-packs',
  templateUrl: 'packs.component.html',
  styleUrls: ['packs.component.scss'],
})
export class PacksComponent implements OnInit {
  branchName = 'main';
  packName = '';
  clientName = '';

  newWorkflowToOldWorkflowMap = new Map();
  workflowIdToWorkflow = new Map();

  currentEnvironment = {};
  workflowsToLoad = [];

  diffStyle: 'diffOnly' | 'full' = 'diffOnly';
  environmentName: 'staging' | 'production' = 'staging';

  loadFromLabel = 'Workflows to load from';

  packsForBranch: string[] = [];
  clientsForPack: string[] = [];

  filteredPacks: string[] = [];
  filteredClientNames: string[] = [];

  campaigns = [];

  showNoDifferences = false;

  loadingWorkflows = false;

  constructor(
    private _objectDiff: NgxObjectDiffService,
    private _httpClient: HttpClient,
    private _workflowService: WorkflowService,
    private _packsService: PacksService,
    private _snackBar: MatSnackBar,
    private route: ActivatedRoute,
    private router: Router
  ) {}

  ngOnInit(): void {
    this.route.queryParams
      .pipe(
        switchMap((params) => {
          if (Object.keys(params).length === 0) {
            return this._packsService.getLastLoadedPack();
          } else {
            if (params.branch) {
              this.branchName = params.branch;
            }
            if (params.pack) {
              this.packName = params.pack;
            }
            if (params.client) {
              this.clientName = params.client;
            }

            this.getPackNames();
            this.getClients();
            this.getNewWorkflows();
          }
          return of(true);
        })
      )
      .subscribe({
        next: ({ branch, name, client }) => {
          if (branch) {
            this.branchName = branch;
          }
          if (name) {
            this.packName = name;
          }
          if (client) {
            this.clientName = client;
          }
        },
        error: (err) => {
          this._snackBar.open(
            'Could not get last loaded pack. Make sure you have a GitHub Credential configured.',
            'Close'
          );
        },
      });
  }

  private _lastBranchName = '';
  private _lastPackName = '';

  // View helper functions
  scrollToTop(): void {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  scrollToWorkflow(id): void {
    document.getElementById(id).scrollIntoView({ behavior: 'smooth' });
  }

  copyToClipboard(workflowName: string): void {
    navigator.clipboard.writeText(workflowName).then(
      () => {
        this._snackBar.open('Copied to clipboard', 'Close');
      },
      () => {
        this._snackBar.open('Could not copy to clipboard', 'Close');
      }
    );
  }

  // Packs services functions
  getPackNames(): void {
    if (this.branchName === '') {
      return;
    }

    if (this.branchName === this._lastBranchName) {
      return;
    }

    const parsePackRequest = ({ packs }) => {
      this._lastBranchName = this.branchName;
      this.packsForBranch = packs.sort();
      this.filteredPacks = this.packsForBranch;
    };
    const showError = (e) => {
      let err_msg = '';
      if (e.error?.description) {
        err_msg =
          e.error.description +
          '. Make sure you have a GitHub Credential configured.';
      } else {
        err_msg = 'Could not find this branch.';
      }
      this._snackBar.open(
        e?.error?.description ?? e?.error?.statusText,
        'Close'
      );
    };
    this._packsService
      .getPackNames(this.branchName)
      .subscribe(parsePackRequest, showError);
  }

  getClients(): void {
    if (this.packName === '') {
      return;
    }

    if (this.packName === this._lastPackName) {
      return;
    }

    const parseClientsRequest = ({ clients }) => {
      this._lastPackName = this.packName;
      this.clientsForPack = clients.sort();
      this.filteredClientNames = this.clientsForPack;
    };
    const showError = () => {
      this.clearClientNames();
      this.filteredClientNames = [];
      this._snackBar.open(
        'Could not get clients. This pack may not have any clients associated with it.',
        'Close'
      );
    };

    this._packsService
      .getClients(this.branchName, this.packName)
      .subscribe(parseClientsRequest, showError);
  }

  clearPackNames() {
    this.packName = '';
    this.packsForBranch = [];
    this._lastBranchName = '';
  }

  onPackNameInput() {
    this.clearClientNames();
    if (this.packName.length > 0) {
      this.filteredPacks = this.packsForBranch.filter((pack) =>
        pack.includes(this.packName)
      );
    } else {
      this.filteredPacks = this.packsForBranch;
    }
  }

  onPackNameSelected() {
    this.clearClientNames();
    this.getClients();
  }

  onClientNameInput() {
    if (this.clientName.length > 0) {
      this.filteredClientNames = this.clientsForPack.filter((client) =>
        client.includes(this.clientName)
      );
    } else {
      this.filteredClientNames = this.clientsForPack;
    }
  }

  clearClientNames() {
    this.clientName = '';
    this.clientsForPack = [];
    this._lastPackName = '';
  }

  getNewWorkflows() {
    this.loadingWorkflows = true;
    let queryParams = {};
    if (this.branchName) {
      queryParams = { branch: this.branchName };
    }
    if (this.packName) {
      queryParams = { ...queryParams, pack: this.packName };
    }
    if (this.clientName) {
      queryParams = { ...queryParams, client: this.clientName };
    }
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: queryParams,
    });
    this.newWorkflowToOldWorkflowMap = new Map();
    this.workflowIdToWorkflow = new Map();
    this.currentEnvironment = {};
    this.workflowsToLoad = [];
    this.updateView();

    const grabAllWorkflows = (workflows) => {
      workflows.forEach((workflow) => {
        this.getCurrentWorkflow(workflow);
      });
    };

    const showError = ({ error }) => {
      let message =
        'Could not get workflows. Make sure you have a GitHub Credential configured.';

      if (error?.description.startsWith('env.json')) {
        message = error.description;
      }

      this._snackBar.open(message, 'Close');
    };

    this._packsService
      .getWorkflows(this.branchName, this.packName, this.clientName)
      .pipe(
        map(({ workflows, environment, campaigns }) => {
          this.currentEnvironment = environment;
          this.campaigns = campaigns ?? [];
          return workflows.map(
            (workflow) =>
              new WorkflowFile(
                workflow.filename,
                workflow.path,
                workflow.config
              )
          );
        })
      )
      .pipe(finalize(() => (this.loadingWorkflows = false)))
      .subscribe(grabAllWorkflows, showError);
  }

  getCurrentWorkflow(workflowToLoad: WorkflowFile): void {
    const mapWorkflowToOldWorkflow = (currentWorkflow) => {
      if (this.newWorkflowToOldWorkflowMap.has(workflowToLoad.id)) {
        this.updateView();
        return;
      }

      this.workflowIdToWorkflow.set(workflowToLoad.id, workflowToLoad);
      this.newWorkflowToOldWorkflowMap.set(workflowToLoad.id, currentWorkflow);
      this.updateView();
    };

    const mapWorkflowToNull = (_error) => {
      if (this.newWorkflowToOldWorkflowMap.has(workflowToLoad.id)) {
        this.updateView();
        return;
      }

      this.workflowIdToWorkflow.set(workflowToLoad.id, workflowToLoad);
      this.newWorkflowToOldWorkflowMap.set(workflowToLoad.id, false);
      this.updateView();
    };

    this._workflowService
      .getWorkflow(workflowToLoad.id)
      .subscribe(mapWorkflowToOldWorkflow, mapWorkflowToNull);
  }

  updateView(): void {
    this.workflowsToLoad = [];
    this.newWorkflowToOldWorkflowMap.forEach((oldWorkflow, newWorkflowId) => {
      let clone = (window as any).structuredClone(
        this.workflowIdToWorkflow.get(newWorkflowId)
      );
      const newWorkflow = new WorkflowFile(
        clone.filename,
        clone.path,
        clone.config
      );

      let oldWorkflowConfig = {};
      if (oldWorkflow?.workflow_version) {
        oldWorkflowConfig = { ...oldWorkflow.workflow_version };
        delete oldWorkflowConfig['user'];
        delete oldWorkflowConfig['version'];
        delete oldWorkflowConfig['timestamp'];
        delete oldWorkflowConfig['oauth_user_email'];
        delete oldWorkflowConfig['oauth_user_id'];

        if (oldWorkflowConfig['description'] === '') {
          delete oldWorkflowConfig['description'];
        }

        oldWorkflowConfig['preconditions'] ??= {};
        if (Object.keys(oldWorkflowConfig['preconditions']).length === 0) {
          delete oldWorkflowConfig['preconditions'];
        }

        oldWorkflowConfig['options'] ??= {};
        if (Object.keys(oldWorkflowConfig['options']).length === 0) {
          delete oldWorkflowConfig['options'];
        }

        oldWorkflowConfig['search_params'] ??= {};
        if (Object.keys(oldWorkflowConfig['search_params']).length === 0) {
          delete oldWorkflowConfig['search_params'];
        }

        oldWorkflowConfig['environment'] ??= {};
        if (Object.keys(oldWorkflowConfig['environment']).length === 0) {
          delete oldWorkflowConfig['environment'];
        }

        oldWorkflowConfig['id'] = oldWorkflow.id;
      }

      if (
        Object.keys(this.currentEnvironment).length > 0 &&
        newWorkflow.config.environment_variables?.length
      ) {
        newWorkflow.config['environment'] = {};
        newWorkflow.config['environment']['variables'] = {};
        newWorkflow.config.environment_variables.forEach((variableName) => {
          if (variableName in (this.currentEnvironment['default'] ?? {})) {
            newWorkflow.config['environment']['variables'][variableName] =
              this.currentEnvironment['default'][variableName];
          }

          if (
            variableName in
            (this.currentEnvironment?.[this.environmentName] ?? {})
          ) {
            newWorkflow.config['environment']['variables'][variableName] =
              this.currentEnvironment[this.environmentName][variableName];
          }
        });
      }
      if (
        Array.isArray(newWorkflow.config.search_params) &&
        newWorkflow.config.search_params.length === 0
      ) {
        delete newWorkflow.config.search_params;
      }
      delete newWorkflow.config.environment_variables;
      delete newWorkflow.config.campaign_name;

      const oldWorkflowView = this._objectDiff.objToJsonView(oldWorkflowConfig);
      const newWorkflowView = this._objectDiff.objToJsonView(
        newWorkflow.config
      );
      const diff = this._objectDiff.diff(oldWorkflowConfig, newWorkflow.config);
      const workflowDiffFullView = this._objectDiff.toJsonView(diff);
      const workflowDiffConflictsOnlyView =
        this._objectDiff.toJsonDiffView(diff);

      this.workflowsToLoad.push({
        isNew: oldWorkflow === false,
        oldWorkflow,
        newWorkflow,
        oldWorkflowView,
        newWorkflowView,
        workflowDiffFullView,
        workflowDiffConflictsOnlyView,
      });
    });

    this.loadFromLabel = `${this.packName}${
      this.clientName ? '(' + this.clientName + ')' : ''
    }`;
  }

  loadAll(): void {
    this.workflowsToLoad.forEach((workflow) => {
      this.loadPack(workflow.newWorkflow);
    });
  }

  loadPack(workflow: WorkflowFile, environmentVariables = {}): void {
    const removeFromUI = () => {
      this.workflowIdToWorkflow.delete(workflow.id);
      this.newWorkflowToOldWorkflowMap.delete(workflow.id);
      this.updateView();
    };

    const showError = ({ logs }) => {
      let errorMessage = `Check network tab for more information. Could not load ${workflow.name}.`;
      if (typeof logs !== 'undefined') {
        errorMessage += ' Logs below: \n' + logs.join(',\n');
      }
      this._snackBar.open(errorMessage, 'Close');
    };

    const originalWorkflow = this.workflowIdToWorkflow.get(workflow.id);

    const requestBody = {
      filename: workflow.filename,
      config: {
        ...workflow.config,
        campaign_name: originalWorkflow.config.campaign_name,
      },
      env_variables: environmentVariables,
      campaigns: this.campaigns,
    };

    this._httpClient
      .post(
        environment.settings.backendUrl + '/api/admin/v2/packs',
        requestBody
      )
      .subscribe(removeFromUI, showError);
  }
}
